import React, { FC, Fragment, useState } from 'react';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import AddDentistModal from 'components/add-dentist-modal/add-dentist-modal';
import { CustomerSetting } from 'models/customer-setting';
import { User } from 'models/user';

type LabAddDentistButtonProps = {
    clinicId: string;
    isWhiteLabel: boolean;
    showAddConnectedDentistButton: boolean;
    labClientCustomerSettings: CustomerSetting[];
    onDentistCreation: (dentist: User) => void;
    reFetchClient?: () => void;
    onUserLinked: (user: User) => void;
    variant?: 'contained' | 'outlined';
    fullWidth?: boolean;
    initialLabDefaultClinicId?: string;
};

const LabAddDentistButton: FC<LabAddDentistButtonProps> = ({
    clinicId,
    labClientCustomerSettings,
    onDentistCreation,
    reFetchClient,
    onUserLinked,
    isWhiteLabel = false,
    showAddConnectedDentistButton = false,
    variant,
    fullWidth = false,
    initialLabDefaultClinicId,
}: LabAddDentistButtonProps) => {
    const { t: translate } = useTranslation(['users']);
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [shouldCreateWLUser, setShouldCreateWLUser] = useState<boolean>(false);
    const [showAddDentistButton, setShowAddDentistButton] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleAddBtnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const closeAddDentistModal = () => {
        setOpenDialog(false);
    };

    const openAddDentistModal = (isWhiteLabel: boolean, showAddConnectedDentistButton: boolean) => {
        setShouldCreateWLUser(isWhiteLabel);
        setShowAddDentistButton(showAddConnectedDentistButton);
        setOpenDialog(true);
    };

    const handleDentistCreation = (dentist: User) => {
        closeAddDentistModal();
        onDentistCreation(dentist);
    };

    const handleUserLinked = (user: User) => {
        closeAddDentistModal();
        onUserLinked(user);
    };

    return (
        <Fragment>
            <Box ml={'auto'} mt={1}>
                {isWhiteLabel || showAddConnectedDentistButton ? (
                    <Fragment>
                        <Button
                            aria-controls={open ? 'customer-type-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                            onClick={handleAddBtnClick}
                            startIcon={<AddIcon />}
                            variant={variant}
                            fullWidth={fullWidth}
                        >
                            {translate('users:ADD_DENTIST')}
                        </Button>
                        <Menu
                            sx={{ marginTop: 1 }}
                            id="customer-type-menu"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            MenuListProps={{
                                'aria-labelledby': 'basic-button',
                            }}
                        >
                            <MenuItem sx={{ minWidth: 180 }} onClick={() => openAddDentistModal(false, false)}>
                                {translate('users:MY_DENTIST')}
                            </MenuItem>
                            <MenuItem onClick={() => openAddDentistModal(isWhiteLabel, showAddConnectedDentistButton)}>
                                {translate('users:DENTHUB_DENTIST')}
                            </MenuItem>
                        </Menu>
                    </Fragment>
                ) : (
                    <Button
                        onClick={() => openAddDentistModal(false, false)}
                        startIcon={<AddIcon />}
                        variant={variant}
                        fullWidth={fullWidth}
                    >
                        {translate('users:ADD_DENTIST')}
                    </Button>
                )}
            </Box>
            <AddDentistModal
                handleDentistCreation={handleDentistCreation}
                open={openDialog}
                showAddDentistButton={showAddDentistButton}
                shouldCreateWLUser={shouldCreateWLUser}
                clinicId={clinicId || ''}
                handleClose={closeAddDentistModal}
                labClientCustomerSettings={labClientCustomerSettings}
                reFetchClient={reFetchClient}
                onUserLinked={handleUserLinked}
                initialLabDefaultClinicId={initialLabDefaultClinicId}
            />
        </Fragment>
    );
};

export default LabAddDentistButton;
